import { UserRole } from '../../src/types'
import api from '@/services/api'
import React, { useEffect, useState } from 'react'
import { FiArrowLeft } from 'react-icons/fi'
import { useNavigate, useParams } from 'react-router-dom'
import TherapistBooking from './TherapistBooking'
import { Therapist, Package } from './Utils'
import { Specialization } from '../types/servicePackage'
import SignupHeader from './PatientPages/SignupHeader'
import { useAppSelector } from '@/store'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import StarIcon from '@mui/icons-material/Star'
import PersonIcon from '@mui/icons-material/Person'
import RoomIcon from '@mui/icons-material/Room'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import DescriptionIcon from '@mui/icons-material/Description'
import LanguageIcon from '@mui/icons-material/Language'
import ScheduleIcon from '@mui/icons-material/Schedule'
import PaymentIcon from '@mui/icons-material/Payment'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import {
  Alert,
  Autocomplete,
  Card,
  CardContent,
  Snackbar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Box,
  Typography,
  Button,
  Stack,
  Grid,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
  Avatar,
} from '@mui/material'
import dayjs from 'dayjs'
import { Chat, Person } from '@mui/icons-material'
import { ChatDialog } from '@/chat/components/ChatDialog'

// Use the Package interface from Utils
type PackageOption = Package

type Insurance = {
  _id: string
  payer_name: string
  payer_state: string
  payerid: string
}

type Degree = {
  _id: string
  name: string
  isActive: boolean
}

type Service = {
  _id: string
  name: string
  private_pay_rate?: number
  sessionFee?: number
  session_length?: number
  session_buffer?: number
  travelOnsiteSurcharge?: number
  cancellationWindowHours?: number
  noShowFee?: number
  lateCancellationFee?: number
  modality?: string[]
  short_description?: string
  long_description?: string
  is_accept_insurance?: boolean
  status?: string
  language?: string[]
  service_radius?: number
  tags?: string[]
  specialization?:
  | string
  | {
    _id: string
    name: string
    description?: string
  }
  insurances?: Array<
    | string
    | {
      _id: string
      payer_name: string
      [key: string]: any
    }
  >
}

type TherapistData = {
  _id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  specialization: string[]
  bio: string
  status: string
  gender: string
  addressLine: string
  city: string
  state: string
  zipCode: string
  insuranceAccepted: Insurance[]
  degrees: Degree[]
  yearsOfExperience: number
  profilePhotoUrl: string
  providerNpi: string
  billingNpi: string
  billingPhone: string
  billingAddressLine: string
  billingCity: string
  billingState: string
  billingZipCode: string
  license_state: string
  license_number: string
  license_date: string
  license_expiration: string
  license_status: string
  license_type: string
  isSavedTherapist?: boolean
}

const HeroChevron = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden>
    <path
      d="M6 8l4 4 4-4"
      stroke="#9CA3AF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export default function TherapistProfilePage(): JSX.Element {
  const { therapistId } = useParams<{ therapistId: string }>()
  const navigate = useNavigate()
  const [selectedPackageId, setSelectedPackageId] = useState<string>('p1')
  const [packages, setPackages] = useState<PackageOption[]>([])
  const [activeTab, setActiveTab] = useState<'about' | 'reviews'>('about')
  const [therapistData, setTherapistData] = useState<TherapistData | null>(null)
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [responseData, setResponseData] = useState<any>(null)

  const [showContractModal, setShowContractModal] = useState(false)
  const [selectedTherapist, setSelectedTherapist] = useState<Therapist | null>(
    null
  )
  const [similarProviders, setSimilarProviders] = useState<any[]>([])

  const [savedProviders, setSavedProviders] = useState<any[]>([])
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [packageDetailsDialogOpen, setPackageDetailsDialogOpen] =
    useState(false)
  const [selectedServiceForDetails, setSelectedServiceForDetails] =
    useState<Service | null>(null)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success'
  )
  const [loginDialogOpen, setLoginDialogOpen] = useState(false)
  const [chatDialogOpen, setChatDialogOpen] = useState(false)

  const { user } = useAppSelector(state => state.auth)
  const [contractAmount, setContractAmount] = useState<string>('')

  const fetchProfileData = async (therapistId: string) => {
    try {
      setLoading(true)
      setError(null)

      const resp = await api.get(`/contracts/similar-providers/${therapistId}`)
      setSimilarProviders(resp?.data?.results)


      const res = await api.get(`/therapist/get-therapist/${therapistId}`)
      // console.log('API Response:', res.data)

      if (res.data.data) {
        setTherapistData({
          ...res.data.data,
          isSavedTherapist: res.data.isSavedTherapist,
        })

      }
      if (res.data.services) {
        setServices(res.data.services)

        // Transform services into package options for booking section
        const servicePackages = res?.data?.services
          .filter((service: Service) => service.status === 'published')
          .map((service: Service, index: number) => {
            // Handle undefined/null/NaN private_pay_rate
            const payRate = service?.private_pay_rate
            const payRateString = payRate.toString()
            setContractAmount(payRateString)
            const isValidRate =
              payRate != null && !isNaN(payRate) && payRate > 0
            return {
              id: service?._id,
              name: service?.name,
              rate: isValidRate ? `$${payRate}` : null,
              duration: `${service.session_length} minutes`,
              description: service.short_description || service.name,
              type: service.modality?.[0]?.replace('_', ' ') || 'In-Person',
            }
          })

        if (servicePackages.length > 0) {
          setPackages(servicePackages)
          setSelectedPackageId(servicePackages[0].id)
        }
      }
    } catch (err: any) {
      console.error('Error fetching provider profile:', err)
      setError(
        'Failed to fetch provider profile: ' +
        (err.response?.data?.message || err.message)
      )
    } finally {
      setLoading(false)
    }
  }
  const fetchSavedProviders = async () => {
    try {
      setLoading(true)
      setError(null)

      // Make the API call to fetch saved providers
      const resp = await api.post('/patient/saved-provider')
      // console.log('Saved Providers Response:', resp?.data)

      if (resp?.data?.results) {
        // Assuming results is an array of saved providers
        setSavedProviders(resp.data.results)
      } else {
        setError('No saved providers found.')
      }
    } catch (err: any) {
      console.error('Error fetching saved providers:', err)
      setError(
        'Failed to fetch saved providers: ' +
        (err.response?.data?.message || err.message)
      )
    } finally {
      setLoading(false)
    }
  }

  // console.log('therapistData', therapistData)
  // Load therapist data
  useEffect(() => {
    if (therapistId) {
      fetchProfileData(therapistId)
    }
  }, [therapistId])

  // console.log('services-packages', services)

  const handleBookSession = () => {
    // ❗ Block users who are not CLIENT
    if (user?.role !== UserRole.CLIENT) {
      // Open login dialog instead of showing snackbar
      setLoginDialogOpen(true)
      return // ⬅️ Stop execution here
    }

    if (!therapistData) {
      console.error('Missing provider data')
      return
    }

    // Get the selected package
    const selectedPackage =
      packages.find(pkg => pkg.id === selectedPackageId) || null

    // Format therapist data for booking component according to the Therapist interface
    const formattedTherapist: Therapist = {
      id: therapistData._id,
      firstName: therapistData.firstName,
      name: `${therapistData.firstName} ${therapistData.lastName}`,
      title: getCredentials(),
      experience: `${therapistData.yearsOfExperience} years`,
      image: therapistData.profilePhotoUrl || '',
      specialties:
        therapistData.specialization?.map((specialty: any) => specialty.name) ||
        [],
      insurances:
        therapistData.insuranceAccepted?.map(
          insurance => insurance.payer_name
        ) || [],
      rate: getPriceRange(),
      packages: packages,
      city: therapistData.city,
      state: therapistData.state,
      location: `${therapistData.city}, ${therapistData.state}`,
      selectedPackage: selectedPackage,
    }

    setSelectedTherapist(formattedTherapist)
    setShowContractModal(true)
  }

  const handleLoginClick = () => {
    const returnPath = therapistId
      ? `/therapists-details/${therapistId}`
      : window.location.pathname + window.location.search

    navigate('/patient-login', {
      state: {
        returnTo: returnPath,
        therapistId: therapistId,
      },
    })
  }

  const handlePackageSelect = (packageId: string) => {
    setSelectedPackageId(packageId)
  }

  const handleViewSelectedPackage = () => {
    if (!selectedPackageId || !services) return

    const selectedService = services.find(
      (service: Service) => service._id === selectedPackageId
    )

    if (selectedService) {
      setSelectedServiceForDetails(selectedService)
      setPackageDetailsDialogOpen(true)
    }
  }

  const handleClosePackageDetailsDialog = () => {
    setPackageDetailsDialogOpen(false)
    setSelectedServiceForDetails(null)
  }

  // Get the currently selected package
  const selectedPackage =
    packages.find(pkg => pkg.id === selectedPackageId) || null

  // Use API data if available, otherwise use static data
  const data = therapistData
  // console.log('data', data)

  // Calculate price range from services
  const getPriceRange = () => {
    if (services.length === 0) return null

    const prices = services
      .filter(service => service.status === 'published')
      .map(service => service.private_pay_rate)
      .filter(price => price != null && !isNaN(price) && price > 0)

    if (prices.length === 0) return null

    const minPrice = Math.min(...prices)
    const maxPrice = Math.max(...prices)

    // Double-check for NaN values
    if (isNaN(minPrice) || isNaN(maxPrice)) return null

    return minPrice === maxPrice
      ? `$${minPrice}/session`
      : `$${minPrice} - $${maxPrice}/session`
  }

  // Get degrees as string
  const getCredentials = () => {
    if (data?.degrees && data.degrees.length > 0) {
      return data.degrees.map(degree => degree.name).join(', ')
    }
    return '->'
  }

  // Get insurance names
  const getInsuranceNames = () => {
    if (data?.insuranceAccepted && data.insuranceAccepted.length > 0) {
      return data.insuranceAccepted
        .map(insurance => insurance.payer_name)
        .join(', ')
    }
    return 'No insurance or any insurance'
  }
  // const saveProvider = async (therapistId: string) => {
  //   try {
  //     setLoading(true)
  //     setError(null)

  //     // Call the saved-provider API with the therapist's ID
  //     const response = await api.post('/patient/saved-provider', {
  //       therapistId, // Send therapist _id in the body
  //     })

  //     console.log('API Response:', response?.data)
  //     setResponseData(response?.data) // Store the response data if needed

  //     // You can also handle the response further based on your requirements
  //   } catch (err: any) {
  //     console.error('Error saving provider:', err)
  //     setError(
  //       'Failed to save provider: ' +
  //         (err.response?.data?.message || err.message)
  //     )
  //   } finally {
  //     setLoading(false)
  //   }
  // }
  const saveProvider = async (therapistId: string) => {
    try {
      setLoading(true)
      setError(null)

      // Call the saved-provider API with the therapist's ID
      const response = await api.post('/patient/saved-provider', {
        therapistId, // Send therapist _id in the body
      })

      if (response?.data) {
        setTherapistData((prev: TherapistData | null) =>
          prev ? { ...prev, isSavedTherapist: true } : null
        )
        setSnackbarMessage('Provider saved successfully')
        setSnackbarSeverity('success')
        setSnackbarOpen(true)
        setResponseData(response?.data) // Store the response data if needed
      } else {
        setSnackbarMessage('Failed to save provider')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
      }

      // console.log('API Response:', response?.data)
      // You can also handle the response further based on your requirements
    } catch (err: any) {
      console.error('Error saving provider:', err)
      setError(
        'Failed to save provider: ' +
        (err.response?.data?.message || err.message)
      )
      setSnackbarMessage(
        'Failed to save provider: ' +
        (err.response?.data?.message || err.message)
      )
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7FAF9] flex items-center justify-center">
        <div className="text-lg text-slate-600">
          Loading provider profile...
        </div>
      </div>
    )
  }

  // if (error) {
  //   return (
  //     <div className="min-h-screen bg-[#F7FAF9] flex items-center justify-center">
  //       <div className="text-lg text-red-600">{error}</div>
  //     </div>
  //   )
  // }

  // if (!data) {
  //   return (
  //     <div className="min-h-screen bg-[#F7FAF9] flex items-center justify-center">
  //       <div className="text-lg text-red-600">Therapist data not found</div>
  //     </div>
  //   )
  // }

  return (
    <>
      <div className="min-h-screen bg-[#F7FAF9] text-slate-700">
        {/* Header */}
        {user?.role !== UserRole.CLIENT && <SignupHeader />}

        {/* Page main content */}
        <main className="relative px-6 py-0 mx-auto max-w-7xl lg:px-8">
          {/* big mint decorative shape on the top-right */}
          <div className="absolute top-0 right-0 z-0 pointer-events-none -translate-y-1/3">
            <svg
              width="100%"
              height="460"
              viewBox="0 0 720 360"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="430" cy="120" r="290" fill="#E9F7EE" />
              <circle cx="450" cy="80" r="240" fill="#F3FBF5" />
            </svg>
          </div>

          <div className="grid lg:grid-cols-[1.3fr_0.9fr] gap-6 relative mt-3">
            {/* LEFT COLUMN */}
            <section className="space-y-6">
              {/* back link */}
              <div className="flex items-center gap-1 px-0 py-0 mx-auto mb-3 max-w-7xl sm:px-6 lg:px-0">
                <button
                  onClick={() => navigate(-1)}
                  className="flex p-2 text-gray-600 transition-colors hover:text-[#3BAE78] hover:bg-green-50 rounded-2xl cursor-pointer"
                >
                  <FiArrowLeft className="w-5 h-5" />
                </button>

                {/* Only takes width of text */}
                <div
                  className="cursor-pointer hover:text-[#3BAE78] w-auto"
                  onClick={() => navigate(-1)}
                >
                  Back
                </div>
              </div>

              {/* Profile card */}
              <div className="flex flex-col gap-6 bg-transparent rounded-2xl sm:flex-row sm:items-start">
                {/* left: photo + base info */}
                <div className="flex w-full gap-6">
                  <div className="flex-shrink-0">
                    {data.profilePhotoUrl ? (
                      <img
                        src={data.profilePhotoUrl}
                        alt="Provider"
                        className="object-cover border w-42 h-42 rounded-xl border-slate-100"
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: '160px',
                          height: '100%',
                          borderRadius: '10px 0 0 10px',
                          bgcolor: 'primary.main',
                        }}
                      >
                        <Person sx={{ fontSize: 120 }} />
                      </Avatar>
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-2xl font-semibold text-slate-800">
                          {data.firstName} {data.lastName}
                        </h2>
                        <div className="inline-flex items-center gap-2 mt-1">
                          <span className="text-sm font-medium px-3 py-1 rounded-full bg-[#E9F7EE] text-[#0f9b58]">
                            {getCredentials()}, {data.yearsOfExperience} years
                            of experience
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* tags */}
                    {data?.license_type ? (
                      <div className="flex flex-wrap gap-2 mt-4 text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <svg
                            className="w-4 h-4 text-emerald-500"
                            viewBox="0 0 24 24"
                            fill="none"
                            aria-hidden
                          >
                            <path
                              d="M12 20c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z"
                              stroke="#10B981"
                              strokeWidth="1.2"
                              fill="#ECFDF5"
                            />
                            <path
                              d="M9 12l2 2 4-4"
                              stroke="#059669"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span>{data.license_type}</span>
                        </div>
                      </div>
                    ) : null}

                    <p className="mt-3 text-sm text-slate-500">
                      {getInsuranceNames()}
                    </p>

                    {getPriceRange() ? (
                      <p className="mt-4 font-semibold text-slate-800">
                        {getPriceRange()}
                      </p>
                    ) : null}
                  </div>
                </div>

                {/* right: vertical icons */}
                <div className="flex-col items-center justify-start hidden gap-3 pt-2 ml-auto sm:flex">
                  {/* chat */}
                  {user?.role === UserRole.CLIENT && (
                    <button
                      className="flex items-center justify-center w-10 h-10 border rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 bg-white border-slate-100 hover:bg-slate-50 text-slate-500"
                      title="Message provider"
                      onClick={() => setChatDialogOpen(true)}
                    >
                      <Chat className="w-5 h-5" />
                    </button>
                  )}
                  {/* save */}
                  {user?.role === UserRole.CLIENT && (
                    <button
                      className={`flex items-center justify-center w-10 h-10 border rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 ${data?.isSavedTherapist === true
                        ? 'bg-[#E9F7EE] border-[#0f9b58] text-[#0f9b58] hover:bg-[#d4f0e0]'
                        : 'bg-white border-slate-100 hover:bg-slate-50 text-slate-500'
                        }`}
                      title={
                        data?.isSavedTherapist
                          ? 'Provider saved'
                          : 'Save provider'
                      }
                      onClick={
                        data?.isSavedTherapist === true
                          ? () => { saveProvider(data._id); fetchProfileData(data._id) }
                          : () => { saveProvider(data._id); fetchProfileData(data._id) }
                      }
                    >
                      {data?.isSavedTherapist ? (
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden
                        >
                          <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z" />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <path d="M6 2h10a2 2 0 0 1 2 2v16l-7-3-7 3V4a2 2 0 0 1 2-2z" />
                        </svg>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Tabs Section */}
              <div className="bg-white">
                <div className="px-6 border-b border-slate-100">
                  <div className="flex gap-8">
                    <button
                      onClick={() => setActiveTab('about')}
                      className={`relative pb-4 pt-6 font-medium ${activeTab === 'about'
                        ? 'text-slate-800'
                        : 'text-slate-500 cursor-pointer'
                        }`}
                    >
                      <span>About</span>
                      {activeTab === 'about' && (
                        <span className="absolute left-0 right-0 -bottom-px h-1 w-full bg-[#0f9b58] rounded-full"></span>
                      )}
                    </button>

                    {/* <button
                      onClick={() => setActiveTab('reviews')}
                      className={`relative pb-4 pt-6 font-medium ${activeTab === 'reviews'
                        ? 'text-slate-800'
                        : 'text-slate-500 cursor-pointer'
                        }`}
                    >
                      <span>Reviews (15)</span>
                      {activeTab === 'reviews' && (
                        <span className="absolute left-0 right-0 -bottom-px h-1 w-full bg-[#0f9b58] rounded-full"></span>
                      )}
                    </button> */}
                  </div>
                </div>
              </div>

              {/* ABOUT CONTENT - Professional statement */}
              {activeTab === 'about' && data.bio && (
                <div className="px-6 pt-6 pb-8 bg-white rounded-b-lg">
                  <h3 className="font-semibold text-slate-800">
                    Professional statement
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600">
                    {data.bio}
                  </p>
                </div>
              )}

              {/* REVIEWS CONTENT */}
              {activeTab === 'reviews' && (
                <div className="px-6 pt-6 pb-8 space-y-6 bg-white rounded-b-lg">
                  <h3 className="font-semibold text-slate-800">
                    What patients are saying
                  </h3>

                  {[1, 2, 3].map(id => (
                    <div key={id} className="pb-4 border-b border-slate-100">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-slate-800">
                          Sarah W.
                        </div>
                        <div className="text-xs text-slate-500">
                          2 weeks ago
                        </div>
                      </div>
                      <div className="flex items-center mt-2">
                        {Array(5)
                          .fill(0)
                          .map((_, i) => (
                            <svg
                              key={i}
                              className="w-4 h-4 text-yellow-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.974a1 1 0 00.95.69h4.18c.969 0 1.371 1.24.588 1.81l-3.39 2.463a1 1 0 00-.364 1.118l1.287 3.974c.3.921-.755 1.688-1.54 1.118l-3.39-2.462a1 1 0 00-1.176 0l-3.39 2.462c-.784.57-1.838-.197-1.539-1.118l1.287-3.974a1 1 0 00-.364-1.118L2.045 9.4c-.783-.57-.38-1.81.588-1.81h4.18a1 1 0 00.95-.69l1.286-3.973z" />
                            </svg>
                          ))}
                      </div>
                      <p className="mt-3 text-sm text-slate-600">
                        Dr. Maxwell is absolutely amazing! He listens carefully
                        and helps provide practical ways to handle physical
                        therapy needs.
                      </p>
                    </div>
                  ))}

                  <button className="text-[#0f9b58] text-sm font-medium hover:underline">
                    Load more reviews
                  </button>
                </div>
              )}

              {/* License grid & chips */}
              <div className="p-6 bg-white rounded-lg">
                <div className="grid grid-cols-1 gap-6 text-sm sm:grid-cols-2 text-slate-700">
                  <div>
                    <div className="mb-2">
                      <span className="block text-slate-500">NPI number</span>
                      <div className="mt-1 font-medium">{data.providerNpi}</div>
                    </div>
                    <div className="mb-2">
                      <span className="block text-slate-500">
                        Licensed state
                      </span>
                      <div className="mt-1 font-medium">
                        {data.license_state ? data.license_state : 'N/A'}
                      </div>
                    </div>
                    {/* <div className="mb-2">
                      <span className="block text-slate-500">
                        Service region/city
                      </span>
                      <div className="mt-1 font-medium">{data.city}</div>
                    </div> */}
                    <div className="mb-2">
                      <span className="block text-slate-500">
                        License Number
                      </span>
                      <div className="mt-1 font-medium">
                        {data.license_number ? data.license_number : 'N/A'}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="mb-2">
                      <span className="block text-slate-500">
                        License Issued Date
                      </span>
                      <div className="mt-1 font-medium">
                        {data.license_date
                          ? dayjs(data.license_date).format('MM/DD/YYYY')
                          : 'N/A'}
                      </div>
                    </div>
                    <div className="mb-2">
                      <span className="block text-slate-500">
                        License expiration date
                      </span>
                      <div className="mt-1 font-medium">
                        {data.license_expiration
                          ? dayjs(data.license_expiration).format('MM/DD/YYYY')
                          : 'N/A'}
                      </div>
                    </div>
                    <div className="mb-2">
                      <span className="block text-slate-500">
                        License Status
                      </span>
                      <div className="mt-1 font-medium">
                        {data.license_status ? data.license_status : 'N/A'}
                      </div>
                    </div>
                    {/* <div className="mb-2">
                      <span className="block text-slate-500">Sanction</span>
                      <div className="mt-1 font-medium">No</div>
                    </div> */}
                  </div>
                </div>

                {/* Specialized in pills */}
                <div className="mt-6">
                  <div className="mb-3 text-sm text-slate-600">
                    Specialized in
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {data?.specialization.length > 0
                      ? data.specialization.map((specialization: any) => (
                        <div
                          key={specialization._id}
                          className="px-3 py-1 text-sm rounded-full bg-slate-100 text-slate-700"
                        >
                          {specialization.name}
                        </div>
                      ))
                      : null}
                  </div>
                </div>

                {/* Insurances */}
                <div className="mt-6">
                  <div className="mb-3 text-sm text-slate-600">
                    In-network insurances
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {data.insuranceAccepted &&
                      data.insuranceAccepted.length > 0 ? (
                      data.insuranceAccepted.map(insurance => (
                        <div
                          key={insurance._id}
                          className="px-3 py-1 text-sm rounded-full bg-slate-100 text-slate-700"
                        >
                          {insurance.payer_name}
                        </div>
                      ))
                    ) : (
                      <>
                        {['No Insurance Provider available'].map(i => (
                          <div
                            key={i}
                            className="px-3 py-1 text-sm rounded-full bg-slate-100 text-slate-700"
                          >
                            {i}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Similar providers */}
              <div className="space-y-3">
                <h4 className="font-medium text-slate-800">
                  Similar providers you may like
                </h4>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {similarProviders.length === 0 && (
                    <div className="text-sm text-gray-500">
                      No similar providers found.
                    </div>
                  )}

                  {/* Flatten the results array and map over providers */}
                  {similarProviders
                    .flat()
                    .map((provider: any, index: number) => (
                      <div
                        key={provider?._id || index}
                        className="flex flex-col items-start flex-1 gap-2 p-4 bg-white border rounded-xl border-slate-100"
                      >
                        {/* Top info row */}
                        <div className="flex gap-3">
                          {provider?.profilePhotoUrl ? (
                            <img
                              src={provider?.profilePhotoUrl}
                              alt="similar"
                              className="object-cover w-16 h-16 rounded-lg"
                            />
                          ) : (
                            <Avatar
                              sx={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '10px',
                                bgcolor: 'primary.main',
                              }}
                            >
                              <Person sx={{ fontSize: 40 }} />
                            </Avatar>
                          )}

                          <span>
                            <div className="font-semibold text-slate-800">
                              {provider?.firstName} {provider?.lastName}
                            </div>

                            <small className="text-xs px-2 py-1 rounded bg-[#F0FBF5] text-[#0f9b58]">
                              {/* Specialization (show first specialization) */}
                              {provider?.specialization?.[0]?.name ||
                                'Specialist'}

                              {/* Experience */}
                              {provider?.yearsOfExperience
                                ? `, ${provider.yearsOfExperience} years of experience`
                                : ''}
                            </small>
                          </span>
                        </div>

                        {/* Lower info row */}
                        <div className="flex-col flex-1">
                          <div className="flex items-center justify-between">
                            <span>
                              {/* Insurances */}
                              <div className="text-xs text-[#0f9b58] mt-1 flex items-center gap-1">
                                <HealthAndSafetyIcon
                                  style={{ height: '16px' }}
                                  className="text-[#26A65B]"
                                />
                                {provider?.insuranceAccepted?.length
                                  ? provider.insuranceAccepted
                                    .map((ins: any) => ins.payer_name)
                                    .slice(0, 2) // Limit to 2 insurance providers
                                    .join(', ')
                                  : 'Insurance info unavailable'}
                              </div>

                              {/* Specializations / Tags */}
                              <div className="text-xs text-[#0f9b58] mt-0 flex items-center gap-1">
                                {provider?.license_type ? (
                                  <>
                                    <StarIcon
                                      style={{ height: '16px' }}
                                      className="text-[#26A65B]"
                                    />
                                    <div className="text-sm px-3 py-1 rounded-full bg-[#F0FBF5] text-[#0f9b58]">
                                      {provider.license_type}
                                    </div>
                                  </>
                                ) : null}
                              </div>

                              {/* Note: Fee information is not available in your data structure */}
                              {/* If you have fee data elsewhere, you'll need to add it */}
                              <div className="flex items-center justify-between gap-4 mt-2">
                                <b className="text-sm text-slate-800">
                                  {provider?.per_session
                                    ? `${provider.per_session}`
                                    : 'Fee not available'}
                                </b>

                                <button
                                  onClick={() => {
                                    user?.role !== UserRole.CLIENT
                                      ? navigate(
                                        `/therapists-profile-details/${provider?._id}`
                                      )
                                      : navigate(
                                        `/therapists-details/${provider?._id}`
                                      )
                                  }}
                                  className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                                >
                                  View Profile
                                </button>
                              </div>
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </section>

            {/* RIGHT COLUMN: Booking card (sticky) */}
            <aside className="relative">
              <div className="sticky top-8">
                <div className="max-w-[520px] bg-white rounded-2xl border border-slate-100 p-6 shadow-md mx-auto">
                  <h3 className="text-lg font-semibold text-center text-slate-800">
                    Book a service
                  </h3>

                  <div className="pb-4 mt-5 border-b border-slate-100">
                    <div className="flex items-start justify-between">
                      <div className="text-sm text-slate-500">
                        Scheduling details
                      </div>
                      <div className="font-semibold text-slate-800">
                        {(() => {
                          if (!selectedPackage?.rate) return 'Price unavailable'
                          // Remove dollar sign, spaces, and parse as number
                          const rateStr = selectedPackage.rate
                            .toString()
                            .replace(/[$,\s]/g, '')
                          const rateNum = parseFloat(rateStr)
                          // Check if valid number
                          if (isNaN(rateNum) || rateNum <= 0)
                            return 'Price unavailable'
                          return `$${rateNum.toFixed(2)}`
                        })()}
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-slate-400">
                      {selectedPackage?.duration || '60 minutes'}
                    </div>
                  </div>

                  <div className="mt-4">
                    {/* <label className="text-xs text-slate-500">
                      Select Insurance
                    </label>
                    <div className="relative mt-2">
                      <div className="flex items-center gap-2 border border-[#E6F4EA] bg-[#F7FBF9] rounded-lg px-3 py-2">
                        <svg
                          className="w-4 h-4 text-[#0f9b58]"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden
                        >
                          <path
                            d="M3 7h18M8 11h8M7 15h10"
                            stroke="#059669"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <select className="w-full text-sm bg-transparent outline-none text-slate-600">
                          <option>
                            Select an insurance to see if you're covered
                          </option>
                          {data.insuranceAccepted &&
                            data.insuranceAccepted.map(insurance => (
                              <option key={insurance._id}>
                                {insurance.payer_name}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div> */}

                    <div className="flex items-center justify-between mt-4 text-xs">
                      <div className="flex items-center gap-2 text-slate-500">
                        <svg
                          className="w-4 h-4 text-slate-400"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden
                        >
                          <path
                            d="M12 2v20M2 12h20"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>Select Package</span>
                      </div>
                      {selectedPackage && (
                        <button
                          onClick={handleViewSelectedPackage}
                          className="text-xs text-[#0f9b58] font-semibold hover:underline cursor-pointer"
                        >
                          View Selected Package
                        </button>
                      )}
                    </div>

                    {/* package list */}
                    <div className="flex gap-3 mt-4 mb-4 overflow-x-auto flex-nowrap scrollbar-thin">
                      {packages.map(pkg => {
                        const isSelected = pkg.id === selectedPackageId

                        return (
                          <button
                            key={pkg.id}
                            onClick={() => handlePackageSelect(pkg.id)}
                            className={`w-[240px] mb-2 flex-shrink-0 text-left flex items-start gap-3 p-3 rounded-lg transition border
                              ${isSelected ? 'border-[#CDF5DF] bg-[#E9F7EE]' : 'border-slate-200 bg-white'}`}
                          >
                            {/* Radio Indicator */}
                            <div className="flex-shrink-0">
                              <div
                                className={`w-6 h-6 rounded-full flex items-center justify-center ${isSelected
                                  ? 'bg-[#0f9b58] text-white'
                                  : 'bg-slate-100 text-slate-600 cursor-pointer'
                                  }`}
                              >
                                {isSelected ? (
                                  <svg
                                    className="w-4 h-4"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M20 6L9 17l-5-5"
                                      stroke="white"
                                      strokeWidth="1.6"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    className="w-4 h-4"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <circle
                                      cx="12"
                                      cy="12"
                                      r="9"
                                      stroke="#9CA3AF"
                                      strokeWidth="1.2"
                                    />
                                  </svg>
                                )}
                              </div>
                            </div>

                            {/* Package Info */}
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-medium text-slate-800">
                                  {pkg.rate || 'Price unavailable'}{' '}
                                  <span className="text-xs text-slate-400">
                                    {' '}
                                    {pkg.duration}
                                  </span>
                                </div>
                              </div>
                              <div className="mt-1 text-xs text-slate-600">
                                {pkg.name}
                              </div>
                              <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                                <svg
                                  className="w-2 h-2"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <circle
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="gray"
                                    strokeWidth="1.2"
                                  />
                                </svg>
                                <span>{pkg.type}</span>
                              </div>
                            </div>
                          </button>
                        )
                      })}
                    </div>

                    <div className="mt-5">
                      <button
                        onClick={handleBookSession}
                        className="w-full text-white bg-[#0f9b58] hover:bg-[#0e8c4e] py-3 rounded-lg font-medium cursor-pointer"
                        disabled={!selectedPackage}
                      >
                        Book Session
                      </button>
                    </div>
                  </div>
                </div>

                {/* subtle rounded white border to reflect screenshot spacing */}
                <div className="mt-4 text-xs text-center text-slate-400"> </div>
              </div>
            </aside>
          </div>
        </main>
      </div>

      {showContractModal && selectedTherapist && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[9999] overflow-hidden">
          <TherapistBooking
            therapistId={selectedTherapist.id}
            therapistData={selectedTherapist}
            selectedPackage={selectedTherapist.selectedPackage}
            contractAmount={contractAmount}
            defaultDate={null}
            onClose={() => setShowContractModal(false)}
            onComplete={booking => {
              console.log('Booking Completed:', booking)
              setShowContractModal(false)
            }}
          />
        </div>
      )}
      {/* Login Required Dialog */}
      <Dialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 3,
            position: 'relative',
            px: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 500, color: 'grey.700' }}>
            Login Required to Book Session
          </Typography>
          <IconButton
            onClick={() => setLoginDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 24,
              top: 24,
              color: '#26a65b',
              '&:hover': {
                bgcolor: 'rgba(38, 166, 91, 0.08)',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ px: 3, py: 2 }}>
          {therapistData && (
            <Box
              sx={{
                mb: 3,
                p: 2.5,
                borderRadius: 2,
                bgcolor: 'rgba(38, 166, 91, 0.04)',
                border: '1px solid rgba(38, 166, 91, 0.15)',
              }}
            >
              <Typography
                variant="overline"
                sx={{ color: 'grey.500', letterSpacing: 1, fontWeight: 600 }}
              >
                Provider Profile
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: 'grey.900', mb: 1 }}
              >
                {therapistData.firstName} {therapistData.lastName}
              </Typography>
              <Stack spacing={1} sx={{ mb: 1.5 }}>
                {therapistData.city && therapistData.state && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RoomIcon sx={{ fontSize: '1rem', color: '#26a65b' }} />
                    <Typography variant="body2" sx={{ color: 'grey.700' }}>
                      {therapistData.city}, {therapistData.state}
                    </Typography>
                  </Box>
                )}
                {therapistData.specialization &&
                  therapistData.specialization.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HealthAndSafetyIcon
                        sx={{ fontSize: '1rem', color: '#26a65b' }}
                      />
                      <Typography variant="body2" sx={{ color: 'grey.700' }}>
                        {therapistData.specialization.slice(0, 3).join(', ')}
                        {therapistData.specialization.length > 3 && '...'}
                      </Typography>
                    </Box>
                  )}
                {getPriceRange() ? (
                  <Typography
                    variant="caption"
                    sx={{ color: 'grey.600', fontWeight: 600 }}
                  >
                    {getPriceRange()}
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{ color: 'grey.600', fontWeight: 600 }}
                  >
                    Price unavailable
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          <Paper
            elevation={0}
            sx={{
              p: 4,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: 'grey.800', mb: 1 }}
            >
              Login Required to Book Session
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'grey.600', lineHeight: 1.6, mb: 3 }}
            >
              Please log in to your patient account or create a new account to
              book a session with this provider and access all booking features.
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonIcon />}
              onClick={handleLoginClick}
              sx={{
                bgcolor: '#26a65b',
                color: 'white',
                borderRadius: 2,
                px: { sm: 2, md: 3 },
                py: 1,
                fontSize: { sm: '0.75rem', md: '0.875rem' },
                fontWeight: 600,
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: '#1e8a4c',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(38, 166, 91, 0.3)',
                },
              }}
            >
              Sign Up / Login
            </Button>
          </Paper>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setLoginDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Service Package Details Dialog */}
      <Dialog
        open={packageDetailsDialogOpen}
        onClose={handleClosePackageDetailsDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.08
            )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
              }}
            >
              <InfoIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Service Package Details
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Complete information about this service
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClosePackageDetailsDialog}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: 'error.main',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {selectedServiceForDetails ? (
            <Box sx={{ p: 3 }}>
              <Stack spacing={3}>
                {/* Header Section */}
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                >
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        gap: 2,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}
                        >
                          {selectedServiceForDetails.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.6 }}
                        >
                          {selectedServiceForDetails.short_description ||
                            'No description available'}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Box>

                {/* Long Description */}
                {selectedServiceForDetails.long_description && (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <DescriptionIcon
                        sx={{ fontSize: 20, color: 'primary.main' }}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: 'text.primary' }}
                      >
                        Detailed Description
                      </Typography>
                    </Box>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2.5,
                        bgcolor: alpha(theme.palette.background.paper, 0.6),
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}
                      >
                        {selectedServiceForDetails.long_description}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                {/* Basic Information Grid */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}
                  >
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Session Fee */}
                    {(() => {
                      const sessionFee =
                        selectedServiceForDetails.sessionFee ||
                        selectedServiceForDetails.private_pay_rate
                      const isValidFee =
                        sessionFee != null &&
                        !isNaN(sessionFee) &&
                        sessionFee > 0
                      return isValidFee ? (
                        <Grid item xs={12} sm={6}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              height: '100%',
                              bgcolor: alpha(theme.palette.success.main, 0.04),
                              borderColor: alpha(
                                theme.palette.success.main,
                                0.2
                              ),
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                            >
                              Session Fee
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: 700, color: 'success.main' }}
                            >
                              ${sessionFee}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              per session
                            </Typography>
                          </Paper>
                        </Grid>
                      ) : null
                    })()}

                    {/* Session Length */}
                    {selectedServiceForDetails.session_length && (
                      <Grid item xs={12} sm={6}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            height: '100%',
                            bgcolor: alpha(theme.palette.secondary.main, 0.04),
                            borderColor: alpha(
                              theme.palette.secondary.main,
                              0.2
                            ),
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <ScheduleIcon
                              sx={{ fontSize: 18, color: 'secondary.main' }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              Session Length
                            </Typography>
                          </Box>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, color: 'secondary.main' }}
                          >
                            {selectedServiceForDetails.session_length} mins
                          </Typography>
                        </Paper>
                      </Grid>
                    )}

                    {/* Session Buffer */}
                    {selectedServiceForDetails.session_buffer != null && (
                      <Grid item xs={12} sm={6}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            height: '100%',
                            bgcolor: alpha(theme.palette.info.main, 0.04),
                            borderColor: alpha(theme.palette.info.main, 0.2),
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <AccessTimeIcon
                              sx={{ fontSize: 18, color: 'info.main' }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              Session Buffer
                            </Typography>
                          </Box>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, color: 'info.main' }}
                          >
                            {selectedServiceForDetails.session_buffer} mins
                          </Typography>
                        </Paper>
                      </Grid>
                    )}

                    {/* Languages */}
                    {selectedServiceForDetails.language &&
                      selectedServiceForDetails.language.length > 0 && (
                        <Grid item xs={12}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              bgcolor: alpha(theme.palette.success.main, 0.04),
                              borderColor: alpha(
                                theme.palette.success.main,
                                0.2
                              ),
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1.5,
                              }}
                            >
                              <LanguageIcon
                                sx={{ fontSize: 18, color: 'success.main' }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                              >
                                Languages Offered
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {selectedServiceForDetails.language.map(
                                (lang, idx) => (
                                  <Chip
                                    key={idx}
                                    label={lang}
                                    size="small"
                                    sx={{
                                      bgcolor: alpha(
                                        theme.palette.success.main,
                                        0.12
                                      ),
                                      color: 'success.main',
                                      fontWeight: 600,
                                      mb: 1,
                                    }}
                                  />
                                )
                              )}
                            </Stack>
                          </Paper>
                        </Grid>
                      )}

                    {/* Service Radius */}
                    {selectedServiceForDetails.service_radius && (
                      <Grid item xs={12} sm={6}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor: alpha(theme.palette.info.main, 0.04),
                            borderColor: alpha(theme.palette.info.main, 0.2),
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <RoomIcon
                              sx={{ fontSize: 18, color: 'info.main' }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              Service Radius
                            </Typography>
                          </Box>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, color: 'info.main' }}
                          >
                            {selectedServiceForDetails.service_radius} miles
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                {/* Fees & Cancellation Policies */}
                {(selectedServiceForDetails.travelOnsiteSurcharge != null ||
                  selectedServiceForDetails.cancellationWindowHours != null ||
                  selectedServiceForDetails.noShowFee != null ||
                  selectedServiceForDetails.lateCancellationFee != null) && (
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <PaymentIcon
                          sx={{ fontSize: 20, color: 'warning.main' }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: 'text.primary' }}
                        >
                          Fees & Cancellation Policies
                        </Typography>
                      </Box>

                      <Grid container spacing={2}>
                        {selectedServiceForDetails.travelOnsiteSurcharge !=
                          null && (
                            <Grid item xs={12} sm={6} md={3}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  height: '100%',
                                  bgcolor: alpha(theme.palette.info.main, 0.04),
                                  borderColor: alpha(theme.palette.info.main, 0.2),
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                                >
                                  Travel/Onsite Surcharge
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 700, color: 'info.main' }}
                                >
                                  $
                                  {selectedServiceForDetails.travelOnsiteSurcharge.toFixed(
                                    2
                                  )}
                                </Typography>
                              </Paper>
                            </Grid>
                          )}

                        {selectedServiceForDetails.cancellationWindowHours !=
                          null && (
                            <Grid item xs={12} sm={6} md={3}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  height: '100%',
                                  bgcolor: alpha(
                                    theme.palette.secondary.main,
                                    0.04
                                  ),
                                  borderColor: alpha(
                                    theme.palette.secondary.main,
                                    0.2
                                  ),
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 1,
                                  }}
                                >
                                  <ScheduleIcon
                                    sx={{ fontSize: 18, color: 'secondary.main' }}
                                  />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    Cancellation Window
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 700, color: 'secondary.main' }}
                                >
                                  {
                                    selectedServiceForDetails.cancellationWindowHours
                                  }{' '}
                                  hrs
                                </Typography>
                              </Paper>
                            </Grid>
                          )}

                        {selectedServiceForDetails.noShowFee != null && (
                          <Grid item xs={12} sm={6} md={3}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                height: '100%',
                                bgcolor: alpha(theme.palette.error.main, 0.04),
                                borderColor: alpha(theme.palette.error.main, 0.2),
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                              >
                                No-Show Fee
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: 700, color: 'error.main' }}
                              >
                                ${selectedServiceForDetails.noShowFee.toFixed(2)}
                              </Typography>
                            </Paper>
                          </Grid>
                        )}

                        {selectedServiceForDetails.lateCancellationFee !=
                          null && (
                            <Grid item xs={12} sm={6} md={3}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  height: '100%',
                                  bgcolor: alpha(theme.palette.warning.main, 0.04),
                                  borderColor: alpha(
                                    theme.palette.warning.main,
                                    0.2
                                  ),
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                                >
                                  Late Cancellation Fee
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 700, color: 'warning.main' }}
                                >
                                  $
                                  {selectedServiceForDetails.lateCancellationFee.toFixed(
                                    2
                                  )}
                                </Typography>
                              </Paper>
                            </Grid>
                          )}
                      </Grid>
                    </Box>
                  )}

                {/* Tags */}
                {selectedServiceForDetails.tags &&
                  selectedServiceForDetails.tags.length > 0 && (
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          Tags
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {selectedServiceForDetails.tags.map((tag, idx) => (
                          <Chip
                            key={idx}
                            label={tag}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.info.main, 0.12),
                              color: 'info.main',
                              fontWeight: 600,
                              mb: 1,
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                {/* Specialization */}
                {selectedServiceForDetails.specialization && (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}
                    >
                      Specialization
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: 'primary.main' }}
                      >
                        {typeof selectedServiceForDetails.specialization ===
                          'object' &&
                          selectedServiceForDetails.specialization !== null
                          ? selectedServiceForDetails.specialization.name ||
                          'Specialization'
                          : 'Specialization'}
                      </Typography>
                      {typeof selectedServiceForDetails.specialization ===
                        'object' &&
                        selectedServiceForDetails.specialization !== null &&
                        selectedServiceForDetails.specialization
                          .description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5, display: 'block' }}
                          >
                            {
                              selectedServiceForDetails.specialization
                                .description
                            }
                          </Typography>
                        )}
                    </Paper>
                  </Box>
                )}

                {/* Insurance Names */}
                {selectedServiceForDetails.insurances &&
                  selectedServiceForDetails.insurances.length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 1.5,
                          color: 'text.primary',
                        }}
                      >
                        Insurance Providers
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1.5,
                          }}
                        >
                          <HealthAndSafetyIcon
                            sx={{ fontSize: 20, color: 'primary.main' }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              fontWeight: 600,
                            }}
                          >
                            Accepted Insurance
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {selectedServiceForDetails.insurances.map(
                            (ins, idx) => {
                              // Handle both ID string and object format
                              const insuranceName =
                                typeof ins === 'object' && ins !== null
                                  ? ins.payer_name
                                  : typeof ins === 'string'
                                    ? ins // Show ID if it's just a string
                                    : 'Insurance'
                              const insuranceId =
                                typeof ins === 'object' && ins !== null
                                  ? ins._id
                                  : typeof ins === 'string'
                                    ? ins
                                    : idx

                              return (
                                <Chip
                                  key={insuranceId || idx}
                                  label={insuranceName}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.12
                                    ),
                                    color: 'primary.main',
                                    fontWeight: 500,
                                  }}
                                />
                              )
                            }
                          )}
                        </Stack>
                      </Paper>
                    </Box>
                  )}
              </Stack>
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Package details are not available.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: alpha(theme.palette.background.paper, 0.6),
          }}
        >
          <Button
            onClick={handleClosePackageDetailsDialog}
            variant="contained"
            sx={{ fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chat Dialog */}
      {user?.role === UserRole.CLIENT && data && (
        <ChatDialog
          open={chatDialogOpen}
          onClose={() => setChatDialogOpen(false)}
          patientId={user?.id ?? ''}
          therapistId={data._id}
          therapistDisplayName={`${data.firstName} ${data.lastName}`}
          therapistAvatarUrl={data.profilePhotoUrl}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
